import React, { useState } from "react";
import { Observer, useLocalStore } from "mobx-react";
import {
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Typography
} from "@material-ui/core";
import { Add, Delete, Edit, RestoreFromTrash } from "@material-ui/icons";
import { store } from "@store";
import { CostCenterSearchStore } from "./cost-center-search-store";
import { CostCenterEditDialog } from "./cost-center-edit-dialog";

const organisationOptions = [
  { value: "", label: "Alla" },
  { value: "Bonliva", label: "Bonliva" },
  { value: "BonlivaCare", label: "Bonliva Care" }
];

export function CostCentersSearch() {
  const localStore = useLocalStore(() => new CostCenterSearchStore());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<any>(null);

  const canEdit = store().user.can("config-editor:update:all");

  const handleCreate = (data: {
    costCenterId: string;
    name: string;
    organisation: string;
    isActive: boolean;
  }) => {
    localStore.create(data);
  };

  const handleUpdate = (data: {
    costCenterId: string;
    name: string;
    organisation: string;
    isActive: boolean;
  }) => {
    if (editItem) {
      localStore.update(editItem._id, {
        name: data.name,
        organisation: data.organisation,
        isActive: data.isActive
      });
    }
  };

  const openCreateDialog = () => {
    setEditItem(null);
    setDialogOpen(true);
  };

  const openEditDialog = (item: any) => {
    setEditItem(item);
    setDialogOpen(true);
  };

  return (
    <Observer>
      {() => (
        <div style={{ padding: 16, flex: 1, maxWidth: 1200, margin: "0 auto", width: "100%" }}>
          <Toolbar disableGutters>
            <Typography variant="h6" style={{ flex: 1 }}>
              Kostnadsställen
            </Typography>
            <TextField
              label="Sök"
              value={localStore.textFilter}
              onChange={(e) => localStore.setTextFilter(e.target.value)}
              size="small"
              variant="outlined"
              style={{ marginRight: 16 }}
              placeholder="Namn eller ID"
            />
            <TextField
              select
              label="Organisation"
              value={localStore.organisationFilter}
              onChange={(e) => localStore.setOrganisationFilter(e.target.value)}
              size="small"
              variant="outlined"
              style={{ marginRight: 16, minWidth: 150 }}
            >
              {organisationOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Checkbox
                  checked={localStore.showDeleted}
                  onChange={(e) => localStore.setShowDeleted(e.target.checked)}
                  size="small"
                />
              }
              label="Visa borttagna"
              style={{ marginRight: 16 }}
            />
            {canEdit && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<Add />}
                onClick={openCreateDialog}
              >
                Skapa
              </Button>
            )}
          </Toolbar>

          <Paper style={{ marginTop: 16 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Namn</TableCell>
                  <TableCell>Organisation</TableCell>
                  <TableCell>Tenant</TableCell>
                  <TableCell>Aktiv</TableCell>
                  {canEdit && <TableCell align="right">Åtgärder</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {localStore.sortedItems.map((item) => (
                  <TableRow
                    key={item._id}
                    style={{
                      backgroundColor: item.isDeleted
                        ? "#ffebee"
                        : item.isActive
                        ? "#e8f5e9"
                        : "#f5f5f5"
                    }}
                  >
                    <TableCell>{item.costCenterId}</TableCell>
                    <TableCell>{item.name}</TableCell>
                    <TableCell>{item.organisation}</TableCell>
                    <TableCell>{item.tenant}</TableCell>
                    <TableCell>
                      {canEdit && !item.isDeleted ? (
                        <Switch
                          checked={item.isActive}
                          onChange={() => localStore.toggleActive(item._id)}
                          size="small"
                          color="primary"
                        />
                      ) : (
                        item.isActive ? "Ja" : "Nej"
                      )}
                    </TableCell>
                    {canEdit && (
                      <TableCell align="right">
                        {!item.isDeleted && (
                          <>
                            <IconButton
                              size="small"
                              onClick={() => openEditDialog(item)}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => localStore.delete(item._id)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </>
                        )}
                        {item.isDeleted && (
                          <IconButton
                            size="small"
                            onClick={() => localStore.restore(item._id)}
                          >
                            <RestoreFromTrash fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {localStore.sortedItems.length === 0 && !localStore.loading && (
                  <TableRow>
                    <TableCell colSpan={canEdit ? 6 : 5} align="center">
                      Inga kostnadsställen hittades
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>

          <CostCenterEditDialog
            open={dialogOpen}
            onClose={() => {
              setDialogOpen(false);
              localStore.fetch();
            }}
            onSubmit={editItem ? handleUpdate : handleCreate}
            initialValues={
              editItem
                ? {
                    costCenterId: editItem.costCenterId,
                    name: editItem.name,
                    organisation: editItem.organisation,
                    isActive: editItem.isActive
                  }
                : undefined
            }
            isEdit={!!editItem}
          />
        </div>
      )}
    </Observer>
  );
}
